import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import { getClients, getUsers, requestProjectReview } from '../../services/api';
import EmailPreviewModal from '../shared/EmailPreviewModal';
import './Projects.css';

const card = { background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' };
const input = { width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none' };
const btn = { padding: '0.75rem 1.5rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: '#020617', fontWeight: 600, borderRadius: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' };

const statusColors = {
  'planning': '#fbbf24',
  'in-progress': '#3b82f6',
  'on-hold': '#f97316',
  'completed': '#10b981',
  'cancelled': '#ef4444'
};

export default function Projects() {
  const { projects, fetchProjects, addProject, updateProject, removeProject, user } = useApp();
  const [clients, setClients] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const isStaffUser = user?.role === 'staff';
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', client: '', description: '', status: 'planning', progress: 0, startDate: '', dueDate: '', assignedUsers: [] });
  const [selectedTeammateId, setSelectedTeammateId] = useState('');
  const [progressDrafts, setProgressDrafts] = useState({});
  const [progressSaving, setProgressSaving] = useState({});
  const [progressErrors, setProgressErrors] = useState({});
  const [statusSaving, setStatusSaving] = useState({});
  const [statusErrors, setStatusErrors] = useState({});
  const [reviewRequestSaving, setReviewRequestSaving] = useState({});
  const [reviewEmailPreviewOpen, setReviewEmailPreviewOpen] = useState(false);
  const [reviewEmailPreviewRequest, setReviewEmailPreviewRequest] = useState(null);
  const [reviewProjectIdToSend, setReviewProjectIdToSend] = useState('');

  useEffect(() => {
    fetchProjects();
    if (!isStaffUser) {
      loadClients();
      loadStaffUsers();
    }
  }, [fetchProjects, isStaffUser]);

  const loadClients = async () => {
    try {
      const data = await getClients();
      setClients(data);
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  };

  const openModal = (project = null) => {
    setEditing(project);
    setSelectedTeammateId('');
    if (project) {
      setForm({
        name: project.name,
        client: project.client._id || project.client,
        description: project.description || '',
        status: project.status,
        progress: project.progress || 0,
        startDate: project.startDate ? project.startDate.split('T')[0] : '',
        dueDate: project.dueDate ? project.dueDate.split('T')[0] : '',
        assignedUsers: (project.assignedUsers || []).map((u) => String(u._id || u))
      });
    } else {
      setForm({ name: '', client: '', description: '', status: 'planning', progress: 0, startDate: '', dueDate: '', assignedUsers: [] });
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.client) return alert('Name and client required');
    try {
      if (editing) {
        await updateProject(editing._id, form);
      } else {
        await addProject(form);
      }
      setShowModal(false);
      fetchProjects();
    } catch (err) {
      alert(err.response?.data?.error || 'Error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this project?')) {
      try {
        await removeProject(id);
        fetchProjects();
      } catch (err) {
        alert('Error deleting project');
      }
    }
  };

  const addAssignedTeammate = () => {
    if (!selectedTeammateId) return;
    setForm((prev) => ({
      ...prev,
      assignedUsers: prev.assignedUsers.includes(selectedTeammateId)
        ? prev.assignedUsers
        : [...prev.assignedUsers, selectedTeammateId]
    }));
    setSelectedTeammateId('');
  };

  const removeAssignedTeammate = (userId) => {
    setForm((prev) => ({
      ...prev,
      assignedUsers: (prev.assignedUsers || []).filter((id) => String(id) !== String(userId))
    }));
  };

  const loadStaffUsers = async () => {
    try {
      const data = await getUsers({ role: 'staff' });
      setStaffUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load staff users:', err);
      setStaffUsers([]);
    }
  };

  const getDisplayedProgress = (project) => {
    const draft = progressDrafts[project._id];
    return draft === undefined ? Number(project.progress || 0) : draft;
  };

  const handleProgressDraftChange = (projectId, nextValue) => {
    const value = Math.max(0, Math.min(100, Number(nextValue || 0)));
    setProgressDrafts((prev) => ({ ...prev, [projectId]: value }));
    setProgressErrors((prev) => {
      if (!prev[projectId]) return prev;
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
  };

  const commitProgress = async (project) => {
    const draft = progressDrafts[project._id];
    if (draft === undefined) return;
    const current = Number(project.progress || 0);
    if (draft === current) return;

    setProgressSaving((prev) => ({ ...prev, [project._id]: true }));
    try {
      await updateProject(project._id, {
        name: project.name,
        description: project.description || '',
        status: project.status || 'planning',
        progress: draft,
        startDate: project.startDate || null,
        dueDate: project.dueDate || null,
        completedDate: project.completedDate || null,
        notes: project.notes || ''
      });
      setProgressDrafts((prev) => {
        const next = { ...prev };
        delete next[project._id];
        return next;
      });
    } catch (err) {
      setProgressErrors((prev) => ({ ...prev, [project._id]: err.response?.data?.error || 'Failed to update progress' }));
    } finally {
      setProgressSaving((prev) => ({ ...prev, [project._id]: false }));
    }
  };

  const updateProjectStatusInline = async (project, nextStatus) => {
    const status = String(nextStatus || '').trim();
    if (!status || status === project.status) return;

    setStatusSaving((prev) => ({ ...prev, [project._id]: true }));
    setStatusErrors((prev) => {
      if (!prev[project._id]) return prev;
      const next = { ...prev };
      delete next[project._id];
      return next;
    });

    try {
      await updateProject(project._id, {
        name: project.name,
        description: project.description || '',
        status,
        progress: Number(project.progress || 0),
        startDate: project.startDate || null,
        dueDate: project.dueDate || null,
        completedDate: project.completedDate || null,
        notes: project.notes || '',
      });
      await fetchProjects();
    } catch (err) {
      setStatusErrors((prev) => ({ ...prev, [project._id]: err.response?.data?.error || 'Failed to update status' }));
    } finally {
      setStatusSaving((prev) => ({ ...prev, [project._id]: false }));
    }
  };

  const openRequestReviewPreview = async (project) => {
    if (!project?._id) return;
    if (project.status !== 'completed') {
      alert('Only completed projects can receive review requests.');
      return;
    }
    const clientId = String(project.client?._id || project.client || '');
    const clientRecord = clients.find((c) => String(c._id) === clientId);
    const clientName = project.clientName || clientRecord?.name || 'Client';
    const clientEmail = clientRecord?.email || '';
    const reviewLink = `${process.env.REACT_APP_CLIENT_URL || 'https://www.pagehyve.com'}/portal?reviewProject=${encodeURIComponent(project._id)}${clientEmail ? `&email=${encodeURIComponent(clientEmail)}` : ''}`;

    setReviewProjectIdToSend(project._id);
    setReviewEmailPreviewRequest({
      type: 'review-request',
      clientName,
      clientEmail,
      projectName: project.name,
      reviewLink
    });
    setReviewEmailPreviewOpen(true);
  };

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700 }}>Projects ({projects.length})</h1>
        {!isStaffUser && <button onClick={() => openModal()} style={btn}><Plus style={{ width: '1rem', height: '1rem' }} /> Add Project</button>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1rem' }}>
        {projects.map(project => (
          <div key={project._id} style={{ ...card, padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{project.name}</h3>
                <p style={{ fontSize: '0.875rem', color: '#94a3b8', margin: 0 }}>
                  {project.clientName || clients.find(c => c._id === (project.client?._id || project.client))?.name || 'Unknown Client'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                {!isStaffUser && <button onClick={() => openModal(project)} style={{ padding: '0.5rem', color: '#94a3b8' }}><Edit style={{ width: '1rem', height: '1rem' }} /></button>}
                {!isStaffUser && <button onClick={() => handleDelete(project._id)} style={{ padding: '0.5rem', color: '#94a3b8' }}><Trash2 style={{ width: '1rem', height: '1rem' }} /></button>}
              </div>
            </div>

            {project.description && (
              <p style={{ fontSize: '0.875rem', color: '#cbd5e1', marginBottom: '1rem', maxHeight: '3em', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.description}</p>
            )}

            {/* Progress */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                <span style={{ color: '#94a3b8' }}>Progress</span>
                <span style={{ color: '#34d399' }}>{getDisplayedProgress(project)}%</span>
              </div>
              <div style={{ width: '100%', height: '0.375rem', background: 'rgba(15,23,42,0.5)', borderRadius: '9999px', overflow: 'hidden' }}>
                <div style={{ width: `${getDisplayedProgress(project)}%`, height: '100%', background: '#34d399', transition: 'width 0.3s ease' }} />
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={getDisplayedProgress(project)}
                onChange={(e) => handleProgressDraftChange(project._id, e.target.value)}
                onMouseUp={() => commitProgress(project)}
                onTouchEnd={() => commitProgress(project)}
                onKeyUp={(e) => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') commitProgress(project); }}
                onBlur={() => commitProgress(project)}
                disabled={!!progressSaving[project._id]}
                style={{ width: '100%', marginTop: '0.55rem' }}
                aria-label={`Update progress for ${project.name}`}
              />
              {progressSaving[project._id] ? (
                <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: '#94a3b8' }}>Updating...</div>
              ) : null}
              {progressErrors[project._id] ? (
                <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: '#f87171' }}>{progressErrors[project._id]}</div>
              ) : null}
            </div>

            {/* Status and Dates */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.875rem' }}>
              <select
                value={project.status || 'planning'}
                onChange={(e) => updateProjectStatusInline(project, e.target.value)}
                disabled={!!statusSaving[project._id]}
                style={{
                  ...input,
                  width: 'auto',
                  minWidth: '8.5rem',
                  padding: '0.35rem 0.55rem',
                  borderRadius: '0.45rem',
                  border: 'none',
                  background: statusColors[project.status] || statusColors.planning,
                  color: '#020617',
                  fontWeight: 600
                }}
                aria-label={`Update status for ${project.name}`}
              >
                <option value="planning">Planning</option>
                <option value="in-progress">In Progress</option>
                <option value="on-hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              {project.dueDate && (
                <span style={{ color: '#94a3b8' }}>Due: {new Date(project.dueDate).toLocaleDateString()}</span>
              )}
            </div>
            {statusSaving[project._id] ? (
              <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: '#94a3b8' }}>Updating status...</div>
            ) : null}
            {statusErrors[project._id] ? (
              <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: '#f87171' }}>{statusErrors[project._id]}</div>
            ) : null}
            {!isStaffUser && project.status === 'completed' ? (
              <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  {project.reviewRequestedAt ? `Review requested on ${new Date(project.reviewRequestedAt).toLocaleDateString()}` : 'No review request sent yet'}
                </span>
                  <button
                    type="button"
                    onClick={() => openRequestReviewPreview(project)}
                    disabled={!!reviewRequestSaving[project._id]}
                    style={{
                      padding: '0.45rem 0.7rem',
                    borderRadius: '0.45rem',
                    border: '1px solid rgba(16,185,129,0.4)',
                      color: '#34d399',
                      fontSize: '0.78rem',
                      opacity: reviewRequestSaving[project._id] ? 0.6 : 1
                    }}
                  >
                  {reviewRequestSaving[project._id] ? 'Sending...' : 'Request Review'}
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {!isStaffUser && showModal && (
        <div className="project-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}>
          <div className="project-modal-shell" style={{ width: '100%', maxWidth: '32rem', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '1rem', padding: '1.5rem' }}>
            <div className="project-modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{editing ? 'Edit' : 'Add'} Project</h2>
              <button onClick={() => setShowModal(false)} style={{ padding: '0.5rem', color: '#94a3b8' }}><X style={{ width: '1.25rem', height: '1.25rem' }} /></button>
            </div>
            <div className="project-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Project Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={input} placeholder="Project name" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Client *</label>
                <select value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} style={input}>
                  <option value="">Select a client...</option>
                  {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...input, minHeight: '100px', resize: 'vertical' }} placeholder="Project description" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={input}>
                  <option value="planning">Planning</option>
                  <option value="in-progress">In Progress</option>
                  <option value="on-hold">On Hold</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Progress: {form.progress}%</label>
                <input type="range" min="0" max="100" value={form.progress} onChange={e => setForm({ ...form, progress: parseInt(e.target.value) })} style={{ width: '100%' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Assigned Teammates</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <select
                    value={selectedTeammateId}
                    onChange={(e) => setSelectedTeammateId(e.target.value)}
                    style={input}
                  >
                    <option value="">Select teammate...</option>
                    {staffUsers
                      .filter((u) => !(form.assignedUsers || []).includes(String(u._id)))
                      .map((u) => <option key={u._id} value={u._id}>{u.email}</option>)
                    }
                  </select>
                  <button type="button" onClick={addAssignedTeammate} style={{ ...btn, padding: '0.75rem 1rem' }}>Add</button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {(form.assignedUsers || []).map((id) => {
                    const u = staffUsers.find((x) => String(x._id) === String(id));
                    return (
                      <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.55rem', borderRadius: '999px', background: 'rgba(16,185,129,0.15)', color: '#34d399', fontSize: '0.8rem' }}>
                        {u?.email || id}
                        <button type="button" onClick={() => removeAssignedTeammate(id)} style={{ color: '#34d399' }}>x</button>
                      </span>
                    );
                  })}
                  {!(form.assignedUsers || []).length && <span style={{ color: '#64748b', fontSize: '0.85rem' }}>No teammates assigned</span>}
                </div>
              </div>
              <div className="project-modal-date-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Start Date</label>
                  <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} style={input} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Due Date</label>
                  <input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} style={input} />
                </div>
              </div>
            </div>
            <div className="project-modal-footer" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: '0.75rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.75rem', color: '#fff' }}>Cancel</button>
              <button onClick={handleSubmit} style={{ ...btn, flex: 1, justifyContent: 'center' }}>{editing ? 'Save' : 'Add'} Project</button>
            </div>
          </div>
        </div>
      )}

      <EmailPreviewModal
        isOpen={reviewEmailPreviewOpen}
        onClose={() => {
          setReviewEmailPreviewOpen(false);
          setReviewEmailPreviewRequest(null);
          setReviewProjectIdToSend('');
        }}
        onConfirm={async (customHtml) => {
          if (!reviewProjectIdToSend) return;
          setReviewRequestSaving((prev) => ({ ...prev, [reviewProjectIdToSend]: true }));
          try {
            await requestProjectReview(reviewProjectIdToSend, {
              customHtml: customHtml || undefined
            });
            await fetchProjects();
            alert('Review request sent to client.');
            setReviewEmailPreviewOpen(false);
            setReviewEmailPreviewRequest(null);
            setReviewProjectIdToSend('');
          } catch (err) {
            alert(err.response?.data?.error || 'Failed to send review request');
          } finally {
            setReviewRequestSaving((prev) => ({ ...prev, [reviewProjectIdToSend]: false }));
          }
        }}
        clientName={reviewEmailPreviewRequest?.clientName}
        clientEmail={reviewEmailPreviewRequest?.clientEmail}
        previewRequest={reviewEmailPreviewRequest}
        isLoading={Boolean(reviewProjectIdToSend && reviewRequestSaving[reviewProjectIdToSend])}
      />
    </div>
  );
}
